import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Checkbox,
  FormControlLabel,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  Block as BlockIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { customersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    contact_salutation: '',
    email: '',
    cc_email: '',
    phone: '',
    address: '',
    postal_code: '',
    city: '',
    country: 'Deutschland',
    tax_id: '',
    notes: '',
    hourly_rate: '',
    daily_rate: '',
  });

  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const { user } = useAuth();

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const fetchCustomers = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await customersAPI.getAll(showInactive);
      setCustomers(response.data);
    } catch (err) {
      setError('Fehler beim Laden der Kunden');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingId) {
        await customersAPI.update(editingId, formData);
        setSuccess('Kunde erfolgreich aktualisiert');
      } else {
        await customersAPI.create(formData);
        setSuccess('Kunde erfolgreich erstellt');
      }

      resetForm();
      fetchCustomers();
      setShowModal(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern des Kunden');
    }
  };

  const handleEdit = (customer) => {
    setFormData({
      company_name: customer.company_name || '',
      contact_person: customer.contact_person || '',
      contact_salutation: customer.contact_salutation || '',
      email: customer.email || '',
      cc_email: customer.cc_email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      postal_code: customer.postal_code || '',
      city: customer.city || '',
      country: customer.country || 'Deutschland',
      tax_id: customer.tax_id || '',
      notes: customer.notes || '',
      hourly_rate: customer.hourly_rate || '',
      daily_rate: customer.daily_rate || '',
    });
    setEditingId(customer.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diesen Kunden wirklich deaktivieren?')) {
      return;
    }

    try {
      await customersAPI.delete(id);
      setSuccess('Kunde erfolgreich deaktiviert');
      fetchCustomers();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Deaktivieren des Kunden');
    }
  };

  const handleReactivate = async (id) => {
    try {
      await customersAPI.update(id, { is_active: true });
      setSuccess('Kunde erfolgreich reaktiviert');
      fetchCustomers();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Reaktivieren des Kunden');
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      contact_person: '',
      contact_salutation: '',
      email: '',
      cc_email: '',
      phone: '',
      address: '',
      postal_code: '',
      city: '',
      country: 'Deutschland',
      tax_id: '',
      notes: '',
      hourly_rate: '',
      daily_rate: '',
    });
    setEditingId(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  if (user.role !== 'admin') {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                color: 'text.secondary',
              }}
            >
              <BlockIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3, color: '#e01e26' }} />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Zugriff verweigert
              </Typography>
              <Typography variant="body1" color="error.main">
                Sie haben keine Berechtigung, auf diese Seite zuzugreifen. Admin-Rechte erforderlich.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight={700}>
          Kundenverwaltung
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateModal}
          sx={{
            background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
            color: 'white',
            fontWeight: 600,
            px: 3,
            py: 1,
            '&:hover': {
              background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
            },
          }}
        >
          Kunde erstellen
        </Button>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Customers Table */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" fontWeight={600}>
              Alle Kunden
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  sx={{
                    color: '#e01e26',
                    '&.Mui-checked': {
                      color: '#e01e26',
                    },
                  }}
                />
              }
              label="Inaktive anzeigen"
            />
          </Box>

          {customers.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                color: 'text.secondary',
              }}
            >
              <BusinessIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" gutterBottom>
                Keine Kunden vorhanden
              </Typography>
              <Typography variant="body2">
                Erstellen Sie Ihren ersten Kunden!
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Kunden-Nr.</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Firma</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ansprechpartner</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Stadt</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>E-Mail</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Telefon</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customers.map((customer) => (
                    <TableRow
                      key={customer.id}
                      sx={{
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                          {customer.customer_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {customer.company_name}
                          </Typography>
                          {!customer.is_active && (
                            <Chip
                              label="Inaktiv"
                              size="small"
                              sx={{
                                bgcolor: '#f5f5f5',
                                color: '#616161',
                                fontSize: '0.7rem',
                                height: '20px',
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {customer.contact_person || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {customer.city || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {customer.email || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {customer.phone || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {customer.is_active ? (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(customer)}
                                sx={{
                                  color: '#1e3a5f',
                                  '&:hover': { bgcolor: 'primary.light' },
                                }}
                                title="Bearbeiten"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(customer.id)}
                                sx={{
                                  color: '#e01e26',
                                  '&:hover': { bgcolor: 'error.light' },
                                }}
                                title="Deaktivieren"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => handleReactivate(customer.id)}
                              sx={{
                                color: '#10b981',
                                '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.1)' },
                              }}
                              title="Reaktivieren"
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog
        open={showModal}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {editingId ? 'Kunde bearbeiten' : 'Kunde erstellen'}
          </Typography>
          <IconButton onClick={closeModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="customer-form" onSubmit={handleSubmit}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                margin="normal"
                label="Firma"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                required
              />

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <FormControl sx={{ minWidth: 120, mt: 2 }}>
                  <InputLabel id="salutation-label">Anrede</InputLabel>
                  <Select
                    labelId="salutation-label"
                    name="contact_salutation"
                    value={formData.contact_salutation}
                    onChange={handleChange}
                    label="Anrede"
                  >
                    <MenuItem value="">
                      <em>Keine</em>
                    </MenuItem>
                    <MenuItem value="Herr">Herr</MenuItem>
                    <MenuItem value="Frau">Frau</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Ansprechpartner"
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={handleChange}
                />
              </Box>

              <TextField
                fullWidth
                margin="normal"
                label="E-Mail"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />

              <TextField
                fullWidth
                margin="normal"
                label="CC E-Mail (für Rechnungsversand)"
                name="cc_email"
                type="email"
                value={formData.cc_email}
                onChange={handleChange}
                helperText="Kopie-Empfänger für Rechnungen/Angebote"
              />

              <TextField
                fullWidth
                margin="normal"
                label="Telefon"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </Box>

            <TextField
              fullWidth
              margin="normal"
              label="Adresse"
              name="address"
              value={formData.address}
              onChange={handleChange}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 2 }}>
              <TextField
                fullWidth
                margin="normal"
                label="PLZ"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
              />

              <TextField
                fullWidth
                margin="normal"
                label="Stadt"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                margin="normal"
                label="Land"
                name="country"
                value={formData.country}
                onChange={handleChange}
              />

              <TextField
                fullWidth
                margin="normal"
                label="Steuernummer / USt-ID"
                name="tax_id"
                value={formData.tax_id}
                onChange={handleChange}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                margin="normal"
                label="Stundensatz (€)"
                name="hourly_rate"
                type="number"
                inputProps={{ step: '0.01', min: '0' }}
                value={formData.hourly_rate}
                onChange={handleChange}
                helperText="Standard-Stundensatz für diesen Kunden"
              />

              <TextField
                fullWidth
                margin="normal"
                label="Tagessatz (€)"
                name="daily_rate"
                type="number"
                inputProps={{ step: '0.01', min: '0' }}
                value={formData.daily_rate}
                onChange={handleChange}
                helperText="Standard-Tagessatz für diesen Kunden"
              />
            </Box>

            <TextField
              fullWidth
              margin="normal"
              label="Notizen"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeModal}
            sx={{
              color: '#1e3a5f',
              fontWeight: 600,
            }}
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            form="customer-form"
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
              },
            }}
          >
            {editingId ? 'Aktualisieren' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CustomersPage;
